import events from "add-event-listener";
import areEqual from "fbjs/lib/areEqual";
import PropTypes from "prop-types";
import * as React from "react";
import { v4 as uuid } from "uuid";

import { NAVIGATION_WIDTH, PAGE_PADDING } from "../../constants";
import { PanelType } from "../../FormParts/Panel/PanelTypes";
import { unitIdContentTypes, UnitIdContext } from "../../FormParts/Unit/UnitIdContext";
import { MultilineRowHeightContextRoot } from "../../Internals/ControlContexts/MultilineRowHeightContext";
import { LayoutEvents, LayoutEventsSubscription } from "../../Internals/LayoutEvents/LayoutEvents";
import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { AnyContext, ofReactType, pullContext } from "../../TypingUtils";
import { ScrollableContainer } from "../ScrollableContainer/ScrollableContainer";
import { UnitId, unitIdToLegacyUnitId } from "../../FormParts/Unit/UnitId/UnitId";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";

import * as styles from "./CrossfitTable.styles";
import { CrossfitTableBodyContainer as CrossfitTableBody } from "./Internals/CrossfitTableBody/CrossfitTableBodyContainer";
import { CrossfitTableBodyMultilineRow } from "./Internals/CrossfitTableBodyRow/CrossfitTableBodyMultilineRow";
import { CrossfitTableBodyRow } from "./Internals/CrossfitTableBodyRow/CrossfitTableBodyRow";
import { CrossfitTableCaptionHeader } from "./Internals/CrossfitTableCaptionHeader/CrossfitTableCaptionHeader";
import { CrossfitTableColumnGroup } from "./Internals/CrossfitTableColumnGroup/CrossfitTableColumnGroup";
import { CrossfitTableHeader } from "./Internals/CrossfitTableHeader/CrossfitTableHeader";
import { CrossfitTableLoader } from "./Internals/CrossfitTableLoader/CrossfitTableLoader";
import { CrossfitTableSidebar } from "./Internals/CrossfitTableSidebar/CrossfitTableSidebar";
import Sticky from "./Internals/HackedControls/Sticky";
import { TablePathProvider } from "./TablePathContext";

export interface CrossfitTableProps {
    bindingPath: AbsoluteModelPath;
    children?: React.ReactNode;
    groupedColumns: Array<[number, number]>;
    hiddenColgroup: number[];
    isSticky: boolean;
    isWidePanel?: boolean;
    loading: boolean;
    panelState: PanelType;
    setUnitTableWidth: (tableId: string, unitId: UnitId, width: number) => void;
    removeUnitTableWidth: (tableId: string, unitId: UnitId) => void;
    hasFilterInfo?: boolean;
    footerHeight: number;
    isReadOnly?: boolean;
    unitsWidth: { [key: string]: { width: number } };
}

interface CrossfitTableState {
    left: number;
}

type CrossfitTableContext = UnitIdContext;

export class CrossfitTable extends React.Component<CrossfitTableProps, CrossfitTableState> {
    public static defaultProps = { isSticky: true };

    public static CaptionHeader = CrossfitTableCaptionHeader;
    public static ColumnGroup = CrossfitTableColumnGroup;
    public static Header = CrossfitTableHeader;
    public static Sidebar = CrossfitTableSidebar;
    public static Body = CrossfitTableBody;
    public static Row = CrossfitTableBodyRow;
    public static MultiRow = CrossfitTableBodyMultilineRow;
    public static Loader = CrossfitTableLoader;

    public static childContextTypes = {
        getHeaderHeight: PropTypes.func,
        getFooterHeight: PropTypes.func,
        getTableWidth: PropTypes.func,
    };
    public static contextTypes = { ...unitIdContentTypes };

    private tableId!: string;
    private unitId?: UnitId;
    private layoutEventsSubscription?: LayoutEventsSubscription;
    private sidebar?: HTMLElement;

    private readonly caption = React.createRef<HTMLDivElement>();
    private readonly captionBackground = React.createRef<HTMLDivElement>();
    private readonly header = React.createRef<HTMLDivElement>();
    private readonly headerStop = React.createRef<HTMLDivElement>();
    private readonly tableContainer = React.createRef<HTMLDivElement>();
    private readonly tableInHeader = React.createRef<HTMLTableElement>();
    private readonly scrollStop = React.createRef<HTMLDivElement>();

    public constructor(props: CrossfitTableProps, context: AnyContext) {
        super(props, context);

        this.state = { left: props.panelState.indexOf("pin") > -1 ? NAVIGATION_WIDTH : PAGE_PADDING };
    }

    public getChildContext = () => ({
        getHeaderHeight: () =>
            this.header.current != undefined && this.caption.current != undefined
                ? this.header.current.clientHeight + this.caption.current.clientHeight
                : 0,
        getFooterHeight: () => this.props.footerHeight,
        getTableWidth: () => (this.tableInHeader.current ? this.tableInHeader.current.clientWidth : 0),
    });

    public componentDidMount(): void {
        this.unitId = this.getUnitId();
        this.tableId = this.getTableId();

        this.layoutEventsSubscription = LayoutEvents.addListener(this.changeLeft);
        events.addEventListener(window, "resize", this.handleRecalc);
        this.handleRecalc();
    }

    public UNSAFE_componentWillReceiveProps(nextProps: CrossfitTableProps): void {
        if (this.unitId != undefined) {
            const legacyUnitId = unitIdToLegacyUnitId(this.unitId);

            if (this.props.unitsWidth[legacyUnitId] !== nextProps.unitsWidth[legacyUnitId]) {
                this.changeLeft();
            }
        }
    }

    public shouldComponentUpdate(nextProps: CrossfitTableProps, nextState: CrossfitTableState): boolean {
        return (
            this.props.isWidePanel !== nextProps.isWidePanel ||
            this.props.panelState !== nextProps.panelState ||
            this.state.left !== nextState.left ||
            this.props.loading !== nextProps.loading ||
            !areEqual(this.props.hiddenColgroup, nextProps.hiddenColgroup) ||
            !areEqual(this.props.groupedColumns, nextProps.groupedColumns)
        );
    }

    public componentDidUpdate(): void {
        this.handleRecalc();
    }

    public componentWillUnmount(): void {
        if (this.layoutEventsSubscription != undefined) {
            this.layoutEventsSubscription.remove();
        }
        events.removeEventListener(window, "resize", this.handleRecalc);

        if (this.unitId) {
            this.props.removeUnitTableWidth(this.tableId, this.unitId);
        }
    }

    public render(): JSX.Element {
        const children = React.Children.toArray(this.props.children);
        const caption = children.find(ofReactType(CrossfitTable.CaptionHeader));
        const colgroup = children.find(ofReactType(CrossfitTable.ColumnGroup));
        const header = children.find(ofReactType(CrossfitTable.Header));
        const sidebar = children.find(ofReactType(CrossfitTable.Sidebar));
        const body = children.find(ofReactType(CrossfitTable.Body));
        const loader = children.find(ofReactType(CrossfitTable.Loader));

        const sidebarColumns = (sidebar && sidebar.props.columns) || 0;

        if (colgroup == undefined || body == undefined) {
            throw new Error("CrossfitTable doesn't have colgroup or body");
        }

        const sidebarColgroup = React.cloneElement(colgroup, { endCol: sidebarColumns });
        const bodyColgroup = React.cloneElement(colgroup, {
            startCol: sidebarColumns,
            cutCols: this.props.groupedColumns,
            hiddenColgroup: this.props.hiddenColgroup,
        });
        const headerGroupedColumns = this.props.groupedColumns.map<[number, number]>(([start, end]) => [
            Number(start) + sidebarColumns,
            Number(end) + sidebarColumns,
        ]);
        const headerColgroup = React.cloneElement(colgroup, {
            cutCols: headerGroupedColumns,
            hiddenColgroup: this.props.hiddenColgroup.map(col => col + sidebarColumns),
        });
        const sidebarElement =
            sidebar &&
            React.cloneElement(sidebar, {
                colgroup: sidebarColgroup,
                isReadOnly: this.props.isReadOnly,
                // @ts-ignore Я не понял, как ref-то передать, ругается на ref
                ref: this.refSidebar,
            });

        const mergedBody = React.cloneElement(body, {
            colgroup: bodyColgroup,
            left: this.state.left,
            hasFilterInfo: this.props.hasFilterInfo,
        });

        const bodyElement = (
            <table css={styles.table} style={sidebar ? {} : { marginLeft: PAGE_PADDING }}>
                {bodyColgroup}
                {mergedBody}
            </table>
        );

        const loaderElement = loader ? React.cloneElement(loader, { active: this.props.loading }) : undefined;

        return (
            <TablePathProvider tablePath={this.props.bindingPath.toLegacyPath()}>
                <MultilineRowHeightContextRoot>
                    <div ref={this.tableContainer}>
                        {this.props.isSticky ? (
                            <ScrollableContainer fixed={sidebarElement} getStop={this.getScrollStop}>
                                <Sticky getStop={this.getStop} side="top" zIndex={105}>
                                    {(fixed: boolean, left: number | "auto") => {
                                        const marginLeft = fixed ? this.state.left - (left === "auto" ? 0 : left) : -80;
                                        const padding = sidebar ? 0 : PAGE_PADDING;
                                        let headerTableStyle;

                                        if (fixed) {
                                            if (sidebar) {
                                                headerTableStyle = { marginLeft: `${marginLeft - PAGE_PADDING}px` };
                                            } else {
                                                headerTableStyle = { marginLeft: `${marginLeft}px` };
                                            }
                                        } else if (sidebar) {
                                            headerTableStyle = {};
                                        } else {
                                            headerTableStyle = { marginLeft: `${PAGE_PADDING - padding}px` };
                                        }

                                        return (
                                            <div css={styles.absoluteWrapHeader(fixed)}>
                                                <div
                                                    css={styles.captionBackground}
                                                    ref={this.captionBackground}
                                                    style={{ marginLeft: marginLeft }}
                                                />
                                                <div
                                                    css={fixed ? styles.fixedCaption : styles.absoluteCaption}
                                                    ref={this.caption}
                                                    style={{
                                                        marginLeft: marginLeft,
                                                        position: "absolute",
                                                    }}>
                                                    <div>{caption}</div>
                                                </div>

                                                <div
                                                    css={styles.headerTable}
                                                    ref={this.header}
                                                    style={headerTableStyle}>
                                                    <div css={styles.fixedHeaderTable(fixed)}>
                                                        <table
                                                            css={themed(
                                                                styles.table,
                                                                !fixed && styles.absoluteHeaderTable
                                                            )}
                                                            ref={this.tableInHeader}
                                                            style={
                                                                fixed
                                                                    ? {
                                                                          marginLeft: `${
                                                                              -(marginLeft + PAGE_PADDING) + padding
                                                                          }px`,
                                                                      }
                                                                    : {}
                                                            }>
                                                            {headerColgroup}
                                                            {header}
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Sticky>
                                <div ref={this.scrollStop} />
                                {loaderElement}
                                {bodyElement}
                            </ScrollableContainer>
                        ) : (
                            <div css={styles.fixedTableWrapper}>{bodyElement}</div>
                        )}
                        <div ref={this.headerStop} />
                    </div>
                </MultilineRowHeightContextRoot>
            </TablePathProvider>
        );
    }

    private readonly handleRecalc = (): void => {
        if (this.header.current && this.sidebar && this.caption.current) {
            const sidebarTop = `${this.header.current.clientHeight + this.caption.current.clientHeight}px`;

            if (this.sidebar.style.top !== sidebarTop) {
                this.sidebar.style.top = sidebarTop;
            }
        }

        if (this.unitId && this.tableInHeader.current) {
            const tableWidth = this.tableInHeader.current.clientWidth + PAGE_PADDING * 2;
            this.props.setUnitTableWidth(this.tableId, this.unitId, tableWidth);
        }
        if (this.captionBackground.current && this.caption.current) {
            this.captionBackground.current.style.height = `${this.caption.current.clientHeight}px`;
        }

        if (this.caption.current && this.tableInHeader.current) {
            const headerWidth = this.tableInHeader.current.clientWidth;
            const { left } = this.state;
            const docWidth = document.documentElement == undefined ? 0 : document.documentElement.clientWidth;
            const width = headerWidth + left + PAGE_PADDING < docWidth ? headerWidth : docWidth - left - PAGE_PADDING;

            this.caption.current.style.width = `${width}px`;
        }
    };

    private getTableId(): string {
        return uuid();
    }

    private getUnitId(): UnitId | undefined {
        return this.getContext().getUnitId?.();
    }

    private getContext(): CrossfitTableContext {
        return pullContext<CrossfitTableContext>(this);
    }

    private readonly changeLeft = () => {
        const left = this.tableContainer.current
            ? this.tableContainer.current.getBoundingClientRect().left
            : PAGE_PADDING;

        this.setState({ left: left });
    };

    private readonly getStop = () => this.headerStop.current;

    private readonly refSidebar = (el: CrossfitTableSidebar | null) => {
        if (el && el.sidebar) {
            this.sidebar = el.sidebar;
        }
    };

    private readonly getScrollStop = () => this.scrollStop.current;
}
